import { useState, useEffect, useRef } from 'react';
import useArrayState from "hooks/useArrayState";
import { SBBox, SBButton, SBForm, SBIcon, SBLoading, SBOutlinedButton, SBSlideMenu, SBTypography } from "modules/Commons";
import { useSnackbar } from "modules/Commons/SBOverlays/SBSnackbar";
import { IForecastModifierConfig } from 'modules/Forecast/models/ForecastModifierConfig';
import { SBNewTable } from 'modules/Commons/SBDataDisplays/SBTable/SBNewTable';
import { useForecastModifierHeaders } from 'modules/Forecast/models/useForecastModifierHeaders';
import ForecastModifierSideMenu from './ForecastModifierSideMenu';
import { useForecastModifierConfigRequester } from 'modules/Forecast/Hooks/useForecastModifierConfigRequester';
import { isBoolean } from 'lodash';
import { Cell } from '@tanstack/react-table';
import ConfigExcludeDialog from 'views/ConfigsView/ConfigExcludeDialog';
import { forecastModifierConfigValidator } from 'domain/Predictive/Services/ForecastModifierConfigValidator';
import { SBTranslate } from 'i18n';

export default () => {
	const { success, error } = useSnackbar();

	const tableRef = useRef<any>();
	const formRef = useRef<any>();

	const forecastModifierConfigRequester = useForecastModifierConfigRequester();

	const [loading, setLoading] = useState<boolean>(false);
	const [loadingSideMenu, setLoadingSideMenu] = useState<boolean>(false);
	const [modifier, setModifier] = useState<IForecastModifierConfig>();
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const [validation, setValidation] = useState<any>({});
	const [isExcluding, setIsExcluding] = useState<boolean>(false);
	const [rows, setRows] = useArrayState<IForecastModifierConfig>([]);
	const [selectedItems, setSelectedItems] = useArrayState([]);

	useEffect(() => {
		setValidation({});
		fetchProcess();
	}, []);

	const headers = useForecastModifierHeaders();

	const handleClick = (cell: Cell<any, any>) => {
		if (cell.column.id === "edit") {
			setModifier(rows.find(r => r.id === cell.row.original.id))
			fetchModifierConfig(cell.row.original.id);
			setIsMenuOpen(true);
		}
	};

	const fetchPropError = (prop: string) => {
		return validation[prop];
	};

	const startNewConfig = () => {
		setLoadingSideMenu(true);
		setValidation({});
		setIsMenuOpen(true);
		setModifier({ id: 0, active: true } as IForecastModifierConfig);
		setTimeout(() => {
			setLoadingSideMenu(false);
		}, 250);
	};

	const fetchModifierConfig = (modifierConfigID: number) => {
		setLoadingSideMenu(true);
		let currentModifier = rows.find(r => r.id === modifierConfigID)
		forecastModifierConfigRequester.GET(`filters/${modifierConfigID}`)
			.then((c: any) => {
				setModifier({
					id: currentModifier?.id,
					name: currentModifier?.name,
					startDate: currentModifier?.startDate,
					endDate: currentModifier?.endDate,
					operationType: currentModifier?.operationType,
					modifier: currentModifier?.modifier,
					active: currentModifier?.active,
					products: c.data.products,
					stores: c.data.stores,
					categories: c.data.categories
				} as IForecastModifierConfig)
			})
			.finally(() => {
				setLoadingSideMenu(false);
			});
	};

	const fetchProcess = async () => {
		let active = true;
		setLoading(true);
		forecastModifierConfigRequester.GET(`filtered/${active}`)
			.then((c: any) => {
				setRows(c.data);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const saveModifier = (newModifier: any) => {
		let creating = false;
		setLoadingSideMenu(true);
		forecastModifierConfigRequester.POST(``, newModifier)
			.then((result: any) => {
				setLoading(true);

				newModifier.products = newModifier.products.map((p: any) => ({
					...p,
					status: isBoolean(p.status) ? p.status : null,
				}));
				newModifier.stores = newModifier.stores.map((p: any) => ({
					...p,
					status: isBoolean(p.status) ? p.status : null,
				}));
				newModifier.categories = newModifier.categories.map((p: any) => ({
					...p,
					status: isBoolean(p.status) ? p.status : null,
				}));

				if (newModifier.id === 0) {
					const newModifierID = result.data ?? 0;
					newModifier.id = newModifierID;
					setRows((d: any) => [...d, newModifier]);
					creating = true;
				} else {
					setRows((rows: any) =>
						rows.triggerChange({
							condition: (row: any) => row.id === newModifier.id,
							newValue: () => newModifier,
						})
					);
				}

				setModifier(newModifier);
				success(
					SBTranslate("modifierCreatedUpdated", {
						status: creating ? SBTranslate("created").toLowerCase() : SBTranslate("updated").toLowerCase(),
					})
				);
			})
			.catch(() => {
				error(SBTranslate("couldntSaveModifier"));
			})
			.finally(() => {
				setLoading(false);
				setLoadingSideMenu(false);
				setIsMenuOpen(false);
				fetchProcess();
			});
	};

	const deleteConfigs = () => {
		setLoadingSideMenu(true);
		let modifiersConfigsIDs = selectedItems.map((index) => rows[+index].id)
		forecastModifierConfigRequester.deleteModifiers(modifiersConfigsIDs)
			.then((result: any) => {
				setLoading(true);

				success(
					SBTranslate("modifierDeleted")
				);
			})
			.catch(() => {
				error(SBTranslate("couldntDeleteModifier"));
			})
			.finally(() => {
				setLoading(false);
				setLoadingSideMenu(false);
				fetchProcess();
			});
	};

	return (
		<SBBox height={1} pl={11}>
			<SBLoading loading={loading}>
				<SBBox
					mb={2}
					ml="auto"
					display="flex"
					justifyContent="space-between"
				>
					<SBBox display="flex" alignItems="center">
						<SBButton onClick={() => startNewConfig()}>
							<SBIcon icon="plus-square" color="#fff" />
							<SBTypography
								ml={1}
								fontWeight="700"
								color="white.main"
								textTransform="none"
							>
								{SBTranslate("newModifier")}
							</SBTypography>
						</SBButton>
					</SBBox>
				</SBBox>
				<SBBox
					width={1}
					style={{ transition: "all 0.25s ease", marginTop: "1rem" }}
				>
					<SBNewTable
						ref={tableRef}
						hasSelection
						virtualize
						title={SBTranslate("modifiers")}
						startRows={rows}
						onDataUpdated={setRows}
						defaultColumns={headers}
						containerHeight={214}
						onClick={handleClick}
						onSelect={(_, obj) => setSelectedItems(obj)}
						toolbarContent={({ rowSelection }: any) => (
							Object.keys(rowSelection).length > 0 ?
								<SBBox>
									<SBOutlinedButton color="error" onClick={() => setIsExcluding(true)}>
										<SBBox
											display="flex"
											alignItems="center"
											px={0.5}
											py={0.3}
										>
											<SBIcon icon="trash-2" color="error" />
											<SBTypography
												textTransform="capitalize"
												pl={1}
												mt={0.3}
												fontWeight="700"
											>
												{SBTranslate("exclude")}
											</SBTypography>
										</SBBox>
									</SBOutlinedButton>
								</SBBox>
								:
								<></>
						)}
					/>
				</SBBox>
			</SBLoading>
			<SBSlideMenu open={isMenuOpen} onClose={() => setIsMenuOpen(false)}>
				{modifier && modifier.id >= 0 && !loadingSideMenu ? (
					<SBForm
						key={modifier.id}
						ref={formRef}
						validationSchema={forecastModifierConfigValidator().schema}
						defaultValues={modifier}
						onError={(v: any, e: any) => {
							setValidation([]);
							const errors = Object.keys(v).reduce(
								(a: any, b: any) => ({
									...a,
									[b]: v[b]?.message,
								}),
								{}
							);
							setValidation(errors);
						}}
						onSubmit={(v) => {
							setValidation([]);
							saveModifier(v);
						}}
					>
						<ForecastModifierSideMenu
							fetchPropError={fetchPropError}
							config={modifier}
							onClose={() => {
								setIsMenuOpen(false);
								setValidation([]);
							}}
							onSave={saveModifier}
							loading={loadingSideMenu}
						/>
					</SBForm>
				) : (
					<SBLoading limited loading />
				)}
			</SBSlideMenu>
			<ConfigExcludeDialog
				open={isExcluding}
				countOfItems={selectedItems.length}
				onClose={() => setIsExcluding(false)}
				onConfirm={() => deleteConfigs()}
				onCancel={() => setIsExcluding(false)}
			/>
		</SBBox>
	);
};

